import { useState, useEffect, useCallback } from 'react';

export type SessionColor = 'green' | 'blue' | 'yellow' | 'orange' | null;

export const SESSION_COLORS: { value: SessionColor; label: string; hex: string }[] = [
  { value: null, label: 'None', hex: 'transparent' },
  { value: 'green', label: 'Green', hex: '#22c55e' },
  { value: 'blue', label: 'Blue', hex: '#3b82f6' },
  { value: 'yellow', label: 'Yellow', hex: '#eab308' },
  { value: 'orange', label: 'Orange', hex: '#f97316' },
];

const STORAGE_KEY = 'sessionColors';
const FULL_TAB_STORAGE_KEY = 'sessionColorFullTab';

interface SessionColorsMap {
  [sessionId: string]: SessionColor;
}

interface SessionFullTabMap {
  [sessionId: string]: boolean;
}

const getStoredColors = (): SessionColorsMap => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to load session colors:', error);
    return {};
  }
};

const saveColors = (colors: SessionColorsMap): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
  } catch (error) {
    console.warn('Failed to save session colors:', error);
  }
};

const getStoredFullTab = (): SessionFullTabMap => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = localStorage.getItem(FULL_TAB_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to load session full tab settings:', error);
    return {};
  }
};

const saveFullTab = (fullTabMap: SessionFullTabMap): void => {
  try {
    localStorage.setItem(FULL_TAB_STORAGE_KEY, JSON.stringify(fullTabMap));
  } catch (error) {
    console.warn('Failed to save session full tab settings:', error);
  }
};

export const useSessionColor = (sessionId: string): [SessionColor, (color: SessionColor) => void] => {
  const [color, setColorState] = useState<SessionColor>(() => {
    const colors = getStoredColors();
    return colors[sessionId] || null;
  });

  // Update color when sessionId changes
  useEffect(() => {
    const colors = getStoredColors();
    setColorState(colors[sessionId] || null);
  }, [sessionId]);

  const setColor = useCallback(
    (newColor: SessionColor) => {
      setColorState(newColor);
      const colors = getStoredColors();
      if (newColor === null) {
        delete colors[sessionId];
      } else {
        colors[sessionId] = newColor;
      }
      saveColors(colors);
      // Dispatch custom event so other components can react
      window.dispatchEvent(new CustomEvent('sessionColorChange', { detail: { sessionId, color: newColor } }));
    },
    [sessionId],
  );

  // Listen for changes from other components
  useEffect(() => {
    const handleColorChange = (event: CustomEvent<{ sessionId: string; color: SessionColor }>) => {
      if (event.detail.sessionId === sessionId) {
        setColorState(event.detail.color);
      }
    };

    window.addEventListener('sessionColorChange', handleColorChange as EventListener);
    return () => {
      window.removeEventListener('sessionColorChange', handleColorChange as EventListener);
    };
  }, [sessionId]);

  return [color, setColor];
};

export const useSessionColorFullTab = (sessionId: string): [boolean, (fullTab: boolean) => void] => {
  const [fullTab, setFullTabState] = useState<boolean>(() => {
    const fullTabMap = getStoredFullTab();
    return fullTabMap[sessionId] || false;
  });

  const setFullTab = useCallback(
    (newFullTab: boolean) => {
      setFullTabState(newFullTab);
      const fullTabMap = getStoredFullTab();
      if (!newFullTab) {
        delete fullTabMap[sessionId];
      } else {
        fullTabMap[sessionId] = newFullTab;
      }
      saveFullTab(fullTabMap);
      // Dispatch custom event so other components can react
      window.dispatchEvent(new CustomEvent('sessionColorFullTabChange', { detail: { sessionId, fullTab: newFullTab } }));
    },
    [sessionId],
  );

  // Listen for changes from other components
  useEffect(() => {
    const handleFullTabChange = (event: CustomEvent<{ sessionId: string; fullTab: boolean }>) => {
      if (event.detail.sessionId === sessionId) {
        setFullTabState(event.detail.fullTab);
      }
    };

    window.addEventListener('sessionColorFullTabChange', handleFullTabChange as EventListener);
    return () => {
      window.removeEventListener('sessionColorFullTabChange', handleFullTabChange as EventListener);
    };
  }, [sessionId]);

  return [fullTab, setFullTab];
};

export const getSessionColorHex = (color: SessionColor): string => {
  const found = SESSION_COLORS.find((c) => c.value === color);
  return found?.hex || 'transparent';
};

// Get a colored circle character for the title
export const getColorCircle = (color: SessionColor): string => {
  // Using Unicode filled circle ● which renders in a flat style
  return color ? '●' : '';
};

// Create a colored favicon as a data URL
export const createColoredFavicon = (color: SessionColor, fullTab: boolean = false): string | null => {
  if (!color) return null;

  const hex = getSessionColorHex(color);
  if (hex === 'transparent') return null;

  // Create favicon - full square if fullTab, otherwise a circle
  const svg = fullTab
    ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
        <rect width="32" height="32" fill="${hex}" />
      </svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
        <circle cx="16" cy="16" r="14" fill="${hex}" />
      </svg>`;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Set or restore the favicon
export const setFavicon = (url: string | null, originalFavicon?: string): void => {
  let link = document.querySelector('link[rel*=\'icon\']') as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  if (url) {
    link.href = url;
  } else if (originalFavicon) {
    link.href = originalFavicon;
  }
};
